import config from '../../../config/config';
import Fetch from '../../../../core/util/core/Fetch';
import { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import { getUserSession } from "../../../../core/util/core/Session"
import ApprovePreSale from '../Modals/ApprovePreSale';
import ReprovePreSale from '../Modals/ReprovePreSale';

const STATUS_CONFIG = {
    'pending': { label: 'Pendente', bg: '#fff8e1', color: '#f59f00', border: '#ffe082' },
    'approved': { label: 'Aprovado', bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
    'reproved': { label: 'Reprovado', bg: '#fdecea', color: '#c62828', border: '#ef9a9a' },
};

const styles = {
    card: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: '16px',
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '10px',
        padding: '16px 20px',
        marginBottom: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
    },
    icon: (status) => ({
        width: '42px',
        height: '42px',
        borderRadius: '50%',
        background: STATUS_CONFIG[status]?.bg ?? '#f1f3f5',
        border: `2px solid ${STATUS_CONFIG[status]?.border ?? '#dee2e6'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: '18px',
    }),
    tag: (status) => ({
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        background: STATUS_CONFIG[status]?.bg ?? '#f1f3f5',
        color: STATUS_CONFIG[status]?.color ?? '#495057',
        border: `1px solid ${STATUS_CONFIG[status]?.border ?? '#dee2e6'}`,
        letterSpacing: '0.3px',
    }),
    label: {
        fontSize: '11px',
        color: '#adb5bd',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '2px',
        fontWeight: '600',
    },
    value: {
        fontSize: '14px',
        color: '#343a40',
    },
    observation: {
        fontSize: '14px',
        color: '#495057',
        lineHeight: '1.6',
        marginTop: '2px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    reason: {
        fontSize: '11px',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: '600',
    },
    field: {
        minWidth: '120px',
    },
    fieldObs: {
        flex: 1,
    },
    divider: {
        width: '1px',
        background: '#e9ecef',
        alignSelf: 'stretch',
        flexShrink: 0,
    },
    actions: {
        marginLeft: 'auto',
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        alignSelf: 'center'
    },
    buttonApprove: {
        background: '#e8f5e9',
        color: '#2e7d32',
        border: '1px solid #a5d6a7',
        borderRadius: '6px',
        padding: '8px 14px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    buttonReprove: {
        background: '#fdecea',
        color: '#c62828',
        border: '1px solid #ef9a9a',
        borderRadius: '6px',
        padding: '8px 14px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    }
};

const STATUS_ICON = {
    'pending': '⏳',
    'approved': '✅',
    'reproved': '❌',
};

function ApprovalCard({ approval, onApprove, onReprove }) {
    const session = getUserSession();

    const status = approval.status ?? 'pending';

    const canApprove =
        session.user.application_profile_id == approval.core_application_profile_id &&
        approval.status == 'pending' &&
        approval.sale_status == 'pre-sale';

    return (
        <div style={styles.card}>
            <div style={styles.icon(status)}>
                {STATUS_ICON[status] ?? '•'}
            </div>

            <div style={styles.field}>
                <div style={styles.label}>Perfil</div>
                <div style={styles.value}>{approval.profile_name ?? '—'}</div>
            </div>

            <div style={styles.divider} />

            <div style={styles.field}>
                <div style={styles.label}>Status</div>
                <div style={styles.tag(status)}>
                    {STATUS_CONFIG[status]?.label ?? status}
                </div>
            </div>

            <div style={styles.divider} />

            <div style={styles.field}>
                <div style={styles.label}>Aprovador</div>
                <div style={styles.value}>{approval.approver_name ?? '—'}</div>
                {approval.status != 'pending' && <div style={styles.value}>{approval.updated_at ?? '—'}</div>}
            </div>

            <div style={styles.divider} />

            <div style={styles.fieldObs}>
                <div style={styles.label}>Observação</div>
                <div style={styles.observation}>
                    {approval.reason ? (
                        <>
                            <span style={styles.reason}>Motivo:</span> {approval.reason}<br />
                            {approval.observation}
                        </>
                    ) : (
                        approval.observation || '—'
                    )}
                </div>
            </div>

            {canApprove && (
                <div style={styles.actions}>
                    <button
                        style={styles.buttonReprove}
                        onClick={() => onReprove(approval)}
                    >
                        Reprovar
                    </button>

                    <button
                        style={styles.buttonApprove}
                        onClick={() => onApprove(approval)}
                    >
                        Aprovar
                    </button>
                </div>
            )}
        </div>
    );
}

function Approvals({ formValues }) {
    const [approvals, setApprovals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showReproveModal, setShowReproveModal] = useState(false);
    const [selectedApproval, setSelectedApproval] = useState(null);

    const openApproveModal = (approval) => {
        setSelectedApproval(approval);
        setShowApproveModal(true);
    };

    const openReproveModal = (approval) => {
        setSelectedApproval(approval);
        setShowReproveModal(true);
    };

    const handleCloseModal = () => {
        setSelectedApproval(null);
        setShowApproveModal(false);
        setShowReproveModal(false);
        load();
    };

    const endpointGetAll = `${config.endpoints.backend}gci/approval-sales-requests/getBySaleId`;

    const load = async () => {
        if (!formValues?.id) return;

        setIsLoading(true);

        const rows = await Fetch.post(endpointGetAll, {
            filter: { gci_sale_id: formValues.id }
        });

        setApprovals(rows ?? []);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
    }, [formValues]);

    if (isLoading) {
        return (
            <div className="d-flex align-items-center gap-2 p-3 text-muted">
                <Spinner size="sm" /> <span>Carregando aprovações...</span>
            </div>
        );
    }

    if (approvals.length === 0) {
        return (
            <div className="p-3 text-muted fst-italic">
                Venda direta. Não houve processo de aprovação pré-venda.
            </div>
        );
    }

    return (
        <div style={{ padding: '16px' }}>
            {approvals.map((approval) => (
                <ApprovalCard
                    key={approval.id}
                    approval={approval}
                    onApprove={openApproveModal}
                    onReprove={openReproveModal}
                />
            ))}

            <ApprovePreSale
                show={showApproveModal}
                onClose={handleCloseModal}
                saleId={formValues?.id}
                approvalId={selectedApproval?.id}
            />

            <ReprovePreSale
                show={showReproveModal}
                onClose={handleCloseModal}
                saleId={formValues?.id}
                approvalId={selectedApproval?.id}
            />
        </div>
    );
}

export default Approvals;